import { useCallback, useMemo } from "react";
import { useAsync } from "@/shared/hooks/HookBase";
import WorkspaceService from "../services/WorkspaceService";
import type {
  WorkspaceDetail,
  WorkspaceSummary,
  WorkspaceUpsert,
} from "../models/Workspace";

export function useWorkspaces() {
  const service = useMemo(() => new WorkspaceService(), []);
  const { loading, error, execute } = useAsync();

  const getAll = useCallback(
    async (): Promise<WorkspaceSummary[] | void> => execute(() => service.getAll()),
    [execute, service],
  );

  const get = useCallback(
    async (workspaceId: string): Promise<WorkspaceDetail | void> =>
      execute(() => service.get(workspaceId)),
    [execute, service],
  );

  const create = useCallback(
    async (workspace: WorkspaceUpsert): Promise<WorkspaceSummary | void> =>
      execute(() => service.create(workspace), {
        successMessage: "Workspace creado correctamente",
      }),
    [execute, service],
  );

  const update = useCallback(
    async (workspaceId: string, workspace: WorkspaceUpsert): Promise<WorkspaceSummary | void> =>
      execute(() => service.update(workspaceId, workspace), {
        successMessage: "Workspace actualizado correctamente",
      }),
    [execute, service],
  );

  const remove = useCallback(
    async (workspaceId: string): Promise<void> => {
      await execute(() => service.delete(workspaceId), {
        successMessage: "Workspace eliminado. Sus automatizaciones quedaron sin agrupar.",
      });
    },
    [execute, service],
  );

  const assignWorkflow = useCallback(
    async (workspaceId: string, workflowId: string): Promise<void> => {
      await execute(() => service.assignWorkflow(workspaceId, workflowId), {
        successMessage: "Automatización archivada en el workspace",
      });
    },
    [execute, service],
  );

  const removeWorkflow = useCallback(
    async (workspaceId: string, workflowId: string): Promise<void> => {
      await execute(() => service.removeWorkflow(workspaceId, workflowId), {
        successMessage: "Automatización quitada del workspace",
      });
    },
    [execute, service],
  );

  return { loading, error, getAll, get, create, update, remove, assignWorkflow, removeWorkflow };
}
